<template>
  <a
    v-if="link"
    :href="link"
    v-bind="relAttr"
    :target="targetWindow"
    :title="title"
    v-on="$listeners"
    v-click="onClick"
  >
    <slot />
  </a>
  <span v-else v-on="$listeners">
    <slot />
  </span>
</template>

<script>
import { mapActions } from 'vuex'
import { equals, pick } from 'ramda'
import { PROP_TYPES, propValidator } from '@fmpedia/validators'
import { wrapIntoSlashes } from '~/plugins/helper'
import { TARGET } from 'enums/links'
import { decodeHTML } from 'entities'
import { LinkRelAttributeConverter } from '@fmpedia/html-tools'
import {
  BASE_HTML_REL_ATTRIBUTE_VALUES,
  HTML_REL_ATTRIBUTE_VALUE
} from '@fmpedia/enums'
import { HEADER_SCROLL_OFFSET_MOBILE } from 'enums/header-scroll-offset'

const SCROLL_TO_DELAY = 500

export default {
  name: 'ALink',
  props: {
    directive: propValidator([PROP_TYPES.STRING], false, null),
    removeDirective: propValidator([PROP_TYPES.BOOLEAN], false, false),
    openInNewTab: propValidator(
      [PROP_TYPES.BOOLEAN, PROP_TYPES.STRING],
      false,
      null
    ),
    to: propValidator([PROP_TYPES.STRING, PROP_TYPES.OBJECT], false),
    scrollToDelay: propValidator([PROP_TYPES.NUMBER], false, SCROLL_TO_DELAY),
    // /**
    //  * This prop is used for router-link to use "name" property instead of "path"
    //  * In case we received wrong path from backend usually we will be redirected to 404 error page,
    //  * but if first part of path is the same as existing parent route we will be redirected to
    //  * dynamic route _category.vue as dynamic path can be any path. But in such case code on
    //  * _category page will not be executed and we will be redirected to all-news empty page.
    //  */
    // routeByName: {
    //   type: Boolean,
    //   default: false
    // },
    title: {
      type: String
    },
    preventDefault: {
      type: Boolean,
      required: false
    }
  },
  data() {
    return {
      reg: new RegExp(`${this.$env.DOMAIN_URL}/?`, 'i')
    }
  },
  computed: {
    link() {
      if (this.isInnerLink) {
        return this.generateRouterPathByUrl(this.to)
      } else {
        /**
         * We decode HTML entities here. More details in this bug:
         * https://adraba.atlassian.net/browse/FMP-15952
         */
        return decodeHTML(this.to)
      }
    },
    isInnerLink() {
      return this.to ? this.checkIfInnerLink(this.to) : true
    },
    openInNewTabBoolean() {
      try {
        return JSON.parse(this.openInNewTab)
      } catch {
        return null
      }
    },
    targetWindow() {
      if (this.openInNewTabBoolean != null) {
        return this.openInNewTabBoolean ? TARGET.BLANK : TARGET.SELF
      }

      /**
       * The default value for external links should be "open in new tab"
       */
      return this.isInnerLink ? TARGET.SELF : TARGET.BLANK
    },
    relAttr() {
      return this.removeDirective || !this.relProps
        ? {}
        : {
            rel: this.relProps
          }
    },
    relProps() {
      const {
        htmlRelString,
        htmlRelStringWithBaseDirectives
      } = new LinkRelAttributeConverter(this.directive)

      if (this.directive != null) {
        return this.isInnerLink
          ? htmlRelString
          : htmlRelStringWithBaseDirectives
      }

      /**
       * The default value for external links should be "nofollow"
       */
      return this.isInnerLink
        ? ''
        : LinkRelAttributeConverter.generateRelString([
            HTML_REL_ATTRIBUTE_VALUE.NOFOLLOW,
            ...BASE_HTML_REL_ATTRIBUTE_VALUES
          ])
    }
  },
  methods: {
    ...mapActions({
      concurrentRouterPush: 'router/concurrentRouterPush'
    }),
    async onClick(event) {
      this.$emit('click', event)

      if (this.preventDefault) {
        event.preventDefault()
        return
      }

      if (!this.isInnerLink || this.openInNewTabBoolean) return

      const newRoute = this.resolveRoute(this.to)
      const { params, query, name, hash: toHash, path } = newRoute
      const handledHash = this.$helper.removeEndingSlashes(toHash)
      const fromHash = this.$route.hash

      const isTheSameRoute =
        this.checkIfTheSameRoute(name, query, params) ||
        this.checkIfFromPaginatedRouteToTheSame(newRoute)

      if (process.client && isTheSameRoute && !toHash && !fromHash) {
        window.location.reload()
      }

      if (this.$helper.isUrlWithPagination() && name === this.$route.name) {
        window.location.href = this.link
      } else {
        await this.followRouterLink({ path, params, query, handledHash })
      }

      if (isTheSameRoute && !handledHash) {
        this.$scrollTo('body', this.scrollToDelay)
      }

      if (handledHash) {
        this.$nextTick(() => {
          this.$scrollTo(handledHash, this.scrollToDelay, {
            offset: HEADER_SCROLL_OFFSET_MOBILE
          })
        })
      }

      event.preventDefault()
    },
    async followRouterLink({ path, params, query, handledHash }) {
      const args = {
        router: this.$router,
        to: {
          path,
          params,
          query,
          hash: handledHash
        }
      }
      await this.concurrentRouterPush({ args })
    },
    checkIfTheSameRoute(name, query, params) {
      const currentRoute = pick(['name', 'query', 'params'], this.$route)
      return equals({ name, query, params }, currentRoute)
    },
    checkIfFromPaginatedRouteToTheSame(newRoute) {
      const {
        isParentRouteFullPathChanged,
        isFromPaginatedToParent
      } = this.$helper.compareRoutes(this.$route, newRoute)

      return !isParentRouteFullPathChanged && isFromPaginatedToParent
    },
    resolveRoute(url) {
      if (!url) return '/'

      if (typeof url === 'string') {
        const { address, query, hash } = this.$helper.parseUrl(url)

        const addressModifierFn = url => {
          const innerLink = url.replace(this.reg, '') || '/'

          return wrapIntoSlashes(innerLink)
        }
        const link = this.$helper.generateUrlFromObject({
          address,
          addressModifierFn,
          query,
          hash
        })

        return link ? this.$router.resolve(link).resolved : null
      }

      return this.$router.resolve(url).resolved
    },
    checkIfInnerLink(url) {
      return (
        this.reg.test(url) ||
        (!this.checkIfHasProtocol(url) && !this.checkIfEmail(url))
      )
    },
    checkIfHasProtocol(url) {
      return /https?:\/\//.test(url)
    },
    checkIfEmail(url) {
      return /mailto:/.test(url)
    },
    generateRouterPathByUrl(link) {
      const resolvedLink = this.resolveRoute(link)
      if (!resolvedLink) return null

      const url = resolvedLink.fullPath
      const { address, query, hash } = this.$helper.parseUrl(url)

      return this.$helper.generateUrlFromObject({
        address,
        addressModifierFn: wrapIntoSlashes,
        query,
        hash
      })
    }
  }
}
</script>
